import { useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { quantidade } from "@/formato";
import { Botao } from "./Botao";

/*
 * A paginação por cursor (design D13).
 *
 * O cursor vai na URL com ENTRADA no histórico: Próxima é navegação, e o voltar do
 * navegador é a página anterior. O botão Anterior faz o mesmo, e por isso só aparece
 * quando há de fato para onde voltar. Quem abriu um link colado cai no meio da lista
 * sem histórico, e o botão é Primeira página.
 *
 * Sem "Ir para n de N": cursor não sabe a página, e o `OFFSET` que saberia pula linha
 * quando a lista muda. Com uma página só não há o que paginar, e nada aparece: a
 * contagem já está no cabeçalho da lista.
 *
 * O `cursor_invalido` é com `useErroDaPagina`, que a lista chama junto.
 */
export function Paginacao({
  itens,
  total,
  proximoCursor,
}: {
  /** Linhas nesta página. */
  itens: number;
  total: number;
  proximoCursor: string | null;
}) {
  const [parametros, setParametros] = useSearchParams();
  const estado: unknown = useLocation().state;
  const navegar = useNavigate();
  const raiz = useRef<HTMLElement>(null);

  const cursor = parametros.get("cursor");
  const veioDaAnterior = (estado as { veioDaAnterior?: boolean } | null)?.veioDaAnterior === true;

  if (cursor === null && proximoCursor === null) return null;

  /*
   * A área de trabalho rola, e não a janela: sem isto, a página nova abriria na altura
   * da última linha da anterior.
   */
  function irAoTopo() {
    raiz.current?.closest("main")?.scrollTo?.({ top: 0 });
  }

  function proxima() {
    if (proximoCursor === null) return;
    const proximos = new URLSearchParams(parametros);
    proximos.set("cursor", proximoCursor);
    setParametros(proximos, { state: { veioDaAnterior: true } });
    irAoTopo();
  }

  function primeira() {
    const proximos = new URLSearchParams(parametros);
    proximos.delete("cursor");
    setParametros(proximos);
    irAoTopo();
  }

  return (
    <nav ref={raiz} aria-label="Paginação" className="flex items-center gap-2">
      <span className="rotulo tabular">
        {quantidade(itens)} de {quantidade(total)}
      </span>

      <div className="ml-auto flex items-center gap-2">
        {cursor !== null && veioDaAnterior && (
          <Botao
            tom="secundario"
            onClick={() => {
              void navegar(-1);
              irAoTopo();
            }}
          >
            Anterior
          </Botao>
        )}
        {cursor !== null && !veioDaAnterior && (
          <Botao tom="secundario" onClick={primeira}>
            Primeira página
          </Botao>
        )}
        {proximoCursor !== null && (
          <Botao tom="secundario" onClick={proxima}>
            Próxima
          </Botao>
        )}
      </div>
    </nav>
  );
}
